function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from 'react';
import { bool, node, number, shape, oneOf } from 'prop-types';
import styled, { css } from 'styled-components';
import { GEL_MARGIN_BELOW_400PX, GEL_GUTTER_BELOW_600PX, GEL_MARGIN_ABOVE_400PX, GEL_GUTTER_ABOVE_600PX } from '@bbc/gel-foundations/spacings';
import { GEL_GROUP_0_SCREEN_WIDTH_MAX, GEL_GROUP_1_SCREEN_WIDTH_MIN, GEL_GROUP_1_SCREEN_WIDTH_MAX, GEL_GROUP_2_SCREEN_WIDTH_MIN, GEL_GROUP_2_SCREEN_WIDTH_MAX, GEL_GROUP_3_SCREEN_WIDTH_MIN, GEL_GROUP_3_SCREEN_WIDTH_MAX, GEL_GROUP_4_SCREEN_WIDTH_MIN, GEL_GROUP_4_SCREEN_WIDTH_MAX, GEL_GROUP_5_SCREEN_WIDTH_MIN } from '@bbc/gel-foundations/breakpoints';
var groups = {
  group0: {
    min: null,
    max: GEL_GROUP_0_SCREEN_WIDTH_MAX,
    gutterSize: GEL_GUTTER_BELOW_600PX,
    marginSize: GEL_MARGIN_BELOW_400PX
  },
  group1: {
    min: GEL_GROUP_1_SCREEN_WIDTH_MIN,
    max: GEL_GROUP_1_SCREEN_WIDTH_MAX,
    gutterSize: GEL_GUTTER_BELOW_600PX,
    marginSize: GEL_MARGIN_BELOW_400PX
  },
  group2: {
    min: GEL_GROUP_2_SCREEN_WIDTH_MIN,
    max: GEL_GROUP_2_SCREEN_WIDTH_MAX,
    gutterSize: GEL_GUTTER_BELOW_600PX,
    marginSize: GEL_MARGIN_ABOVE_400PX
  },
  group3: {
    min: GEL_GROUP_3_SCREEN_WIDTH_MIN,
    max: GEL_GROUP_3_SCREEN_WIDTH_MAX,
    gutterSize: GEL_GUTTER_ABOVE_600PX,
    marginSize: GEL_MARGIN_ABOVE_400PX
  },
  group4: {
    min: GEL_GROUP_4_SCREEN_WIDTH_MIN,
    max: GEL_GROUP_4_SCREEN_WIDTH_MAX,
    gutterSize: GEL_GUTTER_ABOVE_600PX,
    marginSize: GEL_MARGIN_ABOVE_400PX
  },
  group5: {
    min: GEL_GROUP_5_SCREEN_WIDTH_MIN,
    max: null,
    gutterSize: GEL_GUTTER_ABOVE_600PX,
    marginSize: GEL_MARGIN_ABOVE_400PX
  }
};

var mediaQuery = function mediaQuery(_ref) {
  var min = _ref.min,
      max = _ref.max,
      styles = _ref.styles;

  if (min && max) {
    return "\n      @media (min-width: ".concat(min, ") and (max-width: ").concat(max, ") {\n        ").concat(styles, "\n      }\n    ");
  }

  if (min) {
    return "\n      @media (min-width: ".concat(min, ") {\n        ").concat(styles, "\n      }\n    ");
  }

  if (max) {
    return "\n      @media (max-width: ".concat(max, ") {\n        ").concat(styles, "\n      }\n    ");
  }

  return '';
};

var gridMediaQueries = function gridMediaQueries(_ref2) {
  var columns = _ref2.columns,
      margins = _ref2.margins,
      gridStartOffset = _ref2.gridStartOffset,
      enableGelGutters = _ref2.enableGelGutters;
  var selectedGroups = Object.keys(columns);
  return selectedGroups.map(function (group) {
    return mediaQuery({
      min: groups[group].min,
      max: groups[group].max,
      styles: "\n      width: initial;\n      margin: 0;\n      grid-template-columns: repeat(".concat(columns[group], ", 1fr);\n      grid-column-end: span ").concat(columns[group], ";\n      ").concat(enableGelGutters ? "grid-column-gap: ".concat(groups[group].gutterSize, ";") : "", "\n      ").concat(margins[group] ? "padding: 0 ".concat(groups[group].marginSize, ";") : "", "\n      ").concat(gridStartOffset && gridStartOffset[group] ? "grid-column-start: ".concat(gridStartOffset[group], ";") : "")
    });
  });
};

var startOffsetPercentage = function startOffsetPercentage(columnsGroup, gridStartOffsetGroup) {
  return "".concat(100 / columnsGroup * (gridStartOffsetGroup - 1), "%");
};

var getNegativeOffset = function getNegativeOffset(columnsGroup, parentColumnsGroup, gridStartOffset, gridStartOffsetGroup) {
  var isValidOffset = gridStartOffset && gridStartOffsetGroup && gridStartOffsetGroup < parentColumnsGroup && columnsGroup === parentColumnsGroup; // if fills out whole page

  return isValidOffset ? " - ".concat(startOffsetPercentage(parentColumnsGroup, gridStartOffsetGroup)) : "";
};
/*
 * 1 We vertically align to the top so that sibling
 *   grid items that are placed side-by-side within a row
 *   have their text and images aligned
 */


var childrenFallback = function childrenFallback(item, dir, columnsGroup, marginsGroup, marginSize, parentColumnsGroup, parentEnableGelGutters, gutterSize, gridStartOffset, gridStartOffsetGroup) {
  var negativeOffset = getNegativeOffset(columnsGroup, parentColumnsGroup, gridStartOffset, gridStartOffsetGroup);
  var guttersWithinParent = parentColumnsGroup - 1;
  var guttersWithinItem = columnsGroup - 1;
  var adjustedNumberOfGutters = guttersWithinParent + 1; // This is needed since this current implementation cannot handle a negative margin outside the items.

  return " \n  ".concat(marginsGroup ? "padding: 0 ".concat(marginSize, ";") : "", "\n  ").concat(parentEnableGelGutters ? " \n        width: calc(".concat(columnsGroup, "/").concat(parentColumnsGroup, "*(100% - ").concat(adjustedNumberOfGutters, " * ").concat(gutterSize, ") + ").concat(guttersWithinItem, " * ").concat(gutterSize, " ").concat(negativeOffset, ");\n        ") : "", "\n  ").concat(parentEnableGelGutters && item ? "margin: 0 ".concat(parseFloat(gutterSize) / 2, "rem;") : "", "\n  ").concat(!parentEnableGelGutters ? "width: calc(".concat(100 * columnsGroup / parentColumnsGroup, "%").concat(negativeOffset, ");") : "", "\n  \n  ").concat(gridStartOffsetGroup && gridStartOffsetGroup < parentColumnsGroup ? "margin-".concat(dir === 'ltr' ? 'left' : 'right', ": ").concat(startOffsetPercentage(parentColumnsGroup, gridStartOffsetGroup)) : "", "\n  display: inline-block;\n  vertical-align: top; \n");
};
/* [1] */


var outerGridFallback = function outerGridFallback(dir, columnsGroup, marginsGroup, marginSize, enableGelGutters, gutterSize, gridStartOffset, gridStartOffsetGroup) {
  return "\n  ".concat(marginsGroup ? "padding: 0 ".concat(marginSize, ";") : "", "\n  ").concat(gridStartOffset && gridStartOffsetGroup < columnsGroup ? "margin-".concat(dir === 'ltr' ? 'left' : 'right', ": ").concat(startOffsetPercentage(columnsGroup, gridStartOffsetGroup)) : "");
};

var gridFallbacks = /*#__PURE__*/css(["", ""], function (_ref3) {
  var item = _ref3.item,
      dir = _ref3.dir,
      columns = _ref3.columns,
      margins = _ref3.margins,
      parentColumns = _ref3.parentColumns,
      enableGelGutters = _ref3.enableGelGutters,
      parentEnableGelGutters = _ref3.parentEnableGelGutters,
      gridStartOffset = _ref3.gridStartOffset;
  var isOuterGrid = !parentColumns;
  var selectedGroups = Object.keys(columns);
  return "\n    \n      ".concat(selectedGroups.map(function (group) {
    return "\n            ".concat(mediaQuery({
      min: groups[group].min,
      max: groups[group].max,
      styles: "\n              ".concat(isOuterGrid ? outerGridFallback(dir, columns[group], margins[group], groups[group].marginSize, enableGelGutters, groups[group].gutterSize, gridStartOffset, gridStartOffset[group]) : childrenFallback(item, dir, columns[group], margins[group], groups[group].marginSize, parentColumns[group], parentEnableGelGutters, groups[group].gutterSize, gridStartOffset, gridStartOffset[group]))
    }), "\n          ");
  }).join(''), " \n    ");
});
var GridComponent = /*#__PURE__*/styled.div.withConfig({
  displayName: "GridComponent",
  componentId: "nf79gm-0"
})(["", " @supports (display:grid){", " ", "}"], gridFallbacks, gridMediaQueries, function (_ref4) {
  var item = _ref4.item;
  return item ? "display: block;" : "display: grid; position: initial;";
});

var Grid = function Grid(_ref5) {
  var children = _ref5.children,
      gridStartOffset = _ref5.startOffset,
      otherProps = _objectWithoutProperties(_ref5, ["children", "startOffset"]);

  var renderChildren = function renderChildren() {
    return React.Children.map(children, function (child) {
      if (child) {
        var isNestedGridComponent = child.type === Grid;

        if (isNestedGridComponent) {
          return /*#__PURE__*/React.cloneElement(child, {
            parentColumns: otherProps.columns,
            parentEnableGelGutters: otherProps.enableGelGutters
          });
        }
      }

      return child;
    });
  };

  var renderGridComponent = function renderGridComponent() {
    return /*#__PURE__*/React.createElement(GridComponent, _extends({}, otherProps, {
      gridStartOffset: gridStartOffset
    }), renderChildren());
  };

  return renderGridComponent();
};

Grid.propTypes = {
  children: node.isRequired,
  dir: oneOf(['ltr', 'rtl']),
  columns: shape({
    group1: number.isRequired,
    group2: number.isRequired,
    group3: number.isRequired,
    group4: number.isRequired,
    group5: number.isRequired
  }).isRequired,
  enableGelGutters: bool,
  enableNegativeGelMargins: bool,
  margins: shape({
    group1: bool,
    group2: bool,
    group3: bool,
    group4: bool,
    group5: bool
  }),
  startOffset: shape({
    group1: number,
    group2: number,
    group3: number,
    group4: number,
    group5: number
  }),
  item: bool,
  parentColumns: shape({
    group1: number,
    group2: number,
    group3: number,
    group4: number,
    group5: number
  })
};
Grid.defaultProps = {
  dir: 'ltr',
  enableGelGutters: false,
  margins: {
    group1: false,
    group2: false,
    group3: false,
    group4: false,
    group5: false
  },
  startOffset: {},
  item: false,
  parentColumns: null
};
export default Grid;